// src/components/AppShell.tsx
import React, { useState } from 'react';
import AuthModal from '@/components/AuthModal';
import SubscriptionModal from '@/components/SubscriptionModal';
import AccountPopover from '@/components/AccountPopover';
import WebsiteSelector from '@/components/WebsiteSelector';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Footer from '@/components/Footer';
import styles from '@/pages/HomePage.module.css';
import StickyCtaBar from '@/components/homepage/StickyCtaBar';
import { Menu } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

type AppShellProps = {
  children: React.ReactNode;
};

const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { isAuthenticated, userEmail, signOut } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authDefaultTab, setAuthDefaultTab] = useState<'login' | 'register'>('register');
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const headerRef = React.useRef<HTMLDivElement>(null);

  const handleMobileSignOut = async () => {
    setMobileMenuOpen(false);
    await signOut();
    navigate('/');
  };

  const handleMobileNavClick = (path: string) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  // Watch for html padding changes (from modals) and apply to fixed header
  React.useEffect(() => {
    const observer = new MutationObserver(() => {
      if (headerRef.current) {
        const htmlPadding = window.getComputedStyle(document.documentElement).paddingRight;
        headerRef.current.style.paddingRight = htmlPadding;
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => {
      observer.disconnect();
      if (headerRef.current) {
        headerRef.current.style.paddingRight = '';
      }
    };
  }, []);

  const navLinkClass = (to: string) => {
    const active = pathname === to || pathname.startsWith(`${to}/`);
    return [
      'transition-colors',
      active ? 'text-foreground underline underline-offset-8 decoration-2' : 'text-muted-foreground hover:text-foreground'
    ].join(' ');
  };

  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground selection:bg-primary selection:text-primary-foreground">
      {/* Homepage banner - above nav */}
      {pathname === '/' && (
        <div className="fixed left-0 right-0 top-0 z-[51] bg-primary flex items-center justify-center h-[40px] overflow-hidden">
          <a
            href="https://tally.so/r/lbNMON"
            target="_blank"
            rel="noopener noreferrer"
            className="font-body font-semibold text-[12px] tracking-[0.6px] uppercase text-[#fafafa] hover:opacity-80 transition-opacity"
          >
            Book Your Free AI Visibility Review
          </a>
        </div>
      )}

      {/* Top Nav - Clean Header */}
      <div ref={headerRef} className={`fixed left-0 right-0 z-50 border-b border-border h-16 ${pathname === '/' ? 'top-10 bg-white' : 'top-0 bg-background'}`}>
        <div className="max-w-[1440px] mx-auto px-4 md:px-10 h-16 flex items-center justify-between">
            <div className="flex items-center gap-4 md:gap-6">
                <Link to={isAuthenticated ? '/dashboard' : '/'} className="font-mono font-bold text-base tracking-[0.8px] uppercase hover:opacity-70 transition-opacity">
                    BY<span className="text-primary">LLMS</span>
                </Link>
                {isAuthenticated && <div className="hidden md:block"><WebsiteSelector /></div>}
            </div>

            <div className="flex items-center gap-6 md:gap-10">
                {!isAuthenticated ? (
                    <>
                        {pathname !== '/' && (
                          <div className="hidden md:flex items-center gap-6 md:gap-10">
                            <button
                                onClick={() => setSubscriptionModalOpen(true)}
                                className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-foreground hover:opacity-70 transition-opacity cursor-pointer"
                            >
                                Pricing
                            </button>
                            <div className="bg-border h-16 w-px" />
                            <button
                                onClick={() => {
                                    setAuthDefaultTab('login');
                                    setAuthOpen(true);
                                }}
                                className="font-mono font-medium text-xs tracking-[0.6px] uppercase text-foreground hover:opacity-70 transition-opacity cursor-pointer"
                            >
                                Login
                            </button>
                          </div>
                        )}
                    </>
                ) : (
                    <>
                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex items-center gap-10 text-xs font-mono font-medium tracking-[0.6px] uppercase">
                            <Link to="/dashboard/insights" className={navLinkClass('/dashboard/insights')}>Insights</Link>
                            <Link to="/dashboard/queries" className={navLinkClass('/dashboard/queries')}>Queries</Link>
                            <Link to="/dashboard/llms-txt" className={navLinkClass('/dashboard/llms-txt')}>LLMS.TXT</Link>
                            <AccountPopover isActive={false} />
                        </nav>
                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setMobileMenuOpen(true)}
                            className="md:hidden p-2 hover:opacity-70 transition-opacity"
                            aria-label="Open menu"
                        >
                            <Menu className="h-6 w-6" />
                        </button>
                    </>
                )}
            </div>
        </div>
      </div>

      {/* Mobile Navigation Sheet */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent side="right" className="w-[300px] p-0">
          <SheetHeader className="p-6 border-b border-border">
            <SheetTitle className="font-mono font-bold text-base tracking-[0.8px] uppercase">
              BY<span className="text-primary">LLMS</span>
            </SheetTitle>
          </SheetHeader>
          <div className="flex flex-col p-6">
            {isAuthenticated && (
              <>
                {/* Website Selector in mobile menu */}
                <div className="mb-6">
                  <WebsiteSelector />
                </div>
                <div className="h-px bg-border mb-6" />
                {/* Navigation Links */}
                <nav className="flex flex-col gap-4 mb-6">
                  <button
                    onClick={() => handleMobileNavClick('/dashboard/insights')}
                    className={`text-left font-mono font-medium text-sm tracking-[0.6px] uppercase transition-colors ${
                      pathname.includes('/dashboard/insights') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Insights
                  </button>
                  <button
                    onClick={() => handleMobileNavClick('/dashboard/queries')}
                    className={`text-left font-mono font-medium text-sm tracking-[0.6px] uppercase transition-colors ${
                      pathname.includes('/dashboard/queries') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    Queries
                  </button>
                  <button
                    onClick={() => handleMobileNavClick('/dashboard/llms-txt')}
                    className={`text-left font-mono font-medium text-sm tracking-[0.6px] uppercase transition-colors ${
                      pathname.includes('/dashboard/llms-txt') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    LLMS.TXT
                  </button>
                </nav>
                <div className="h-px bg-border mb-6" />
                {/* Account Section */}
                <div className="flex flex-col gap-4">
                  <p className="text-xs font-mono text-muted-foreground truncate">
                    {userEmail}
                  </p>
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setSubscriptionModalOpen(true);
                    }}
                    className="text-left font-mono font-medium text-sm tracking-[0.6px] uppercase text-foreground hover:text-primary transition-colors"
                  >
                    Billing
                  </button>
                  <a
                    href="https://mail.google.com/mail/?view=cm&to=support@byllms.com&su=WE%20HAVE%20TO%20TALK&body=Call%20me%20asap."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-left font-mono font-medium text-sm tracking-[0.6px] uppercase text-foreground hover:text-primary transition-colors"
                  >
                    Contact Support
                  </a>
                  <button
                    onClick={handleMobileSignOut}
                    className="text-left font-mono font-medium text-sm tracking-[0.6px] uppercase text-foreground hover:text-primary transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Content */}
      <main className={`relative flex-1 ${pathname === '/' ? 'pt-[104px]' : 'pt-20'}`}>
          <div key={pathname} className="animate-page-enter">
            {children}
          </div>
      </main>

      {/* Footer */}
      <Footer />

      {/* Sticky CTA - homepage only, outside animated wrapper */}
      {pathname === '/' && (
        <StickyCtaBar onRequestAccess={() => window.open('https://tally.so/r/lbNMON', '_blank', 'noopener,noreferrer')} />
      )}

      {/* Auth modal */}
      <AuthModal
        open={authOpen}
        onOpenChange={setAuthOpen}
        defaultTab={authDefaultTab}
        onSuccess={() => {
          // If user logged in while on the testing page, keep them here and resume analysis
          if (pathname === '/test-queries') {
            try { localStorage.setItem('resume_analysis', '1'); } catch {}
            setAuthOpen(false);
            return;
          }
          navigate('/dashboard');
        }}
      />

      {/* Subscription modal */}
      <SubscriptionModal
        open={subscriptionModalOpen}
        onOpenChange={setSubscriptionModalOpen}
      />
    </div>
  );
};

export default AppShell;
